import { lazy, Suspense, useEffect, useState } from "react";
import { usePostHog } from "@posthog/react";
import { HarbourGame } from "./components/game/HarbourGame";
import { StoryLanding } from "./components/StoryLanding";
import { PageSite } from "./PageSite";
import { ApplicationPage } from "./ApplicationPage";
import { AutterTestPanel } from "./components/AutterTestPanel";
import { APPLICATION_URL } from "./content";

type View = "game" | "page" | "site" | "apply" | "test";

const AcceptancePage = lazy(() =>
  import("./AcceptancePage").then((module) => ({ default: module.AcceptancePage })),
);

const SEO = {
  game: {
    title: "Same Boat | Community for builders shipping with AI agents",
    description:
      "Same Boat by Autter is a curated community for technical founders and engineers shipping real software with AI agents. Six builders, one table, practical help.",
  },
  page: {
    title: "Same Boat | The story behind the community",
    description:
      "Why Same Boat exists: a letter from the founders about building a room for people who ship software with AI agents.",
  },
  site: {
    title: "Inside Same Boat | A curated AI builder community by Autter",
    description:
      "Meet the community format: six builders matched by problem and timezone, sharing real agent workflows, hard-won lessons and one useful ask each.",
  },
  apply: {
    title: "Apply to Same Boat | Autter",
    description:
      "Request a seat in Same Boat, the curated community for technical founders and engineers who have shipped real software with AI agents in the last 90 days.",
  },
  test: {
    title: "Autter Runtime Test | Same Boat",
    description: "Internal test panel for Autter Runtime — error capture, messages, events, and context.",
  },
} satisfies Record<View, { title: string; description: string }>;

/**
 * Same Boat opens on the full "why this exists" letter. From there the visitor
 * can read the whole site (PageSite), join the community (ApplicationPage),
 * or play the harbour (HarbourGame).
 */
function App() {
  const posthog = usePostHog();
  const acceptanceToken = window.location.pathname.match(/^\/welcome\/([A-Za-z0-9_-]{40,64})\/?$/)?.[1];
  const [view, setView] = useState<View>(() => {
    if (window.location.hash === "#apply") return "apply";
    if (window.location.hash === "#play") return "game";
    if (window.location.hash === "#site") return "site";
    if (window.location.hash === "#test") return "test";
    return "page";
  });

  // Views swap without real navigation, so each is reported as a virtual
  // pageview. The acceptance token stays out of the captured URL.
  useEffect(() => {
    const path =
      acceptanceToken
        ? "/welcome"
        : view === "page" || view === "site"
          ? "/"
          : `/${view}`;
    posthog?.capture("$pageview", { $current_url: `${window.location.origin}${path}` });
  }, [acceptanceToken, view, posthog]);

  const navigate = (next: View) => {
    setView(next);
    const url =
      next === "apply"
        ? "#apply"
        : next === "game"
          ? `${window.location.pathname}${window.location.search}#play`
          : next === "site"
            ? `${window.location.pathname}${window.location.search}#site`
            : `${window.location.pathname}${window.location.search}`;
    window.history.pushState({ sameBoatView: next }, "", url);
  };

  const enterFromAcceptance = () => {
    setView("page");
    window.history.replaceState({ sameBoatView: "page" }, "", "/");
  };

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (APPLICATION_URL !== "#apply" || !(event.target instanceof Element)) return;
      const link = event.target.closest<HTMLAnchorElement>('a[href="#apply"]');
      if (!link) return;
      event.preventDefault();
      navigate("apply");
    };
    const onPopState = (event: PopStateEvent) => {
      const restored = event.state?.sameBoatView as View | undefined;
      if (restored) {
        setView(restored);
        return;
      }
      const hash = window.location.hash;
      setView(hash === "#apply" ? "apply" : hash === "#play" ? "game" : hash === "#site" ? "site" : hash === "#test" ? "test" : "page");
    };
    document.addEventListener("click", onClick);
    window.addEventListener("popstate", onPopState);
    return () => {
      document.removeEventListener("click", onClick);
      window.removeEventListener("popstate", onPopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The game owns the full viewport with no page scroll; the pages scroll.
  useEffect(() => {
    document.body.style.overflow = view === "game" ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [view]);

  useEffect(() => {
    if (acceptanceToken) return;
    const seo = SEO[view];
    document.title = seo.title;
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", seo.description);
    document.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.setAttribute("content", seo.title);
    document.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.setAttribute("content", seo.description);
    document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]')?.setAttribute("content", seo.title);
    document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]')?.setAttribute("content", seo.description);
  }, [acceptanceToken, view]);

  if (acceptanceToken) {
    return (
      <Suspense fallback={<main className="chart-grid min-h-[100dvh] bg-paper" />}>
        <AcceptancePage token={acceptanceToken} onEnter={enterFromAcceptance} />
      </Suspense>
    );
  }
  if (view === "test") return <AutterTestPanel />;
  if (view === "game") return <HarbourGame onReadPage={() => navigate("site")} />;
  if (view === "site") return <PageSite onPlay={() => navigate("game")} />;
  if (view === "page") {
    return (
      <StoryLanding
        onJoin={() => navigate("apply")}
        onPlay={() => navigate("game")}
        onReadMore={() => navigate("site")}
      />
    );
  }
  return <ApplicationPage onBack={() => navigate("page")} onSail={() => navigate("game")} />;
}

export { App };
